import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useKnowledgeMapSubjects } from '../../components/Sidebar/ConceptBuilderSideBar.tsx';
import { ConstantActions } from '../../Services/_actions/constant.actions';
import { globalpermissions } from '../../Services/Permission';
import { userInformationAtom } from '../../Services/_states';
import { CONCEPT_BUILDER_KEY, PRACTICE_TEST_KEY } from './navigation.constants';

/**
 * Dynamic Concept Builder & Practice Test nav — mirrors legacy ModernSidebar behaviour.
 *
 * @param {{ isSuperuser?: boolean }} options
 */
export default function useConceptBuilderNavItems({ isSuperuser = false } = {}) {
  const [fetchTriggered, setFetchTriggered] = useState(false);
  const actions = ConstantActions();
  const userInfo = useRecoilValue(userInformationAtom);

  const isSuperuserCalc =
    isSuperuser ||
    userInfo?.is_superuser === true ||
    userInfo?.profile?.name === 'Admin';

  const hasClassListAccess = actions.userHasPermission([
    globalpermissions?.performancePermissions?.classListAccess,
  ]);

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      (window.location?.pathname?.startsWith('/r/student/concept-builder') ||
        window.location?.pathname?.startsWith('/r/student/create-test'))
    ) {
      setFetchTriggered(true);
    }
  }, []);

  const { loading: subjectsLoading, subjects } = useKnowledgeMapSubjects(fetchTriggered);

  const subjectMenuItems = useMemo(
    () =>
      subjects.map((sub) => ({
        label: sub.label,
        key: sub.value,
        route: `/r/student/concept-builder?subject=${encodeURIComponent(sub.value)}`,
        accessKey: 'conceptBuilder',
      })),
    [subjects]
  );

  const subjectMenuItemsForPracticeTest = useMemo(
    () =>
      subjects.map((sub) => ({
        label: sub.label,
        key: sub.value,
        route: `/r/student/create-test?subject=${encodeURIComponent(sub.value)}`,
        accessKey: 'practiceTest',
      })),
    [subjects]
  );

  const triggerFetch = useCallback(() => {
    if (!fetchTriggered) {
      setFetchTriggered(true);
    }
  }, [fetchTriggered]);

  const conceptBuilderItem = useMemo(() => {
    const base = {
      title: 'Concept Builder',
      iconKey: 'school',
      key: CONCEPT_BUILDER_KEY,
      accessKey: 'conceptBuilder',
      permission: [globalpermissions?.performancePermissions?.conceptBuilder],
    };

    if (isSuperuserCalc || hasClassListAccess) {
      return {
        ...base,
        route: '/r/student/concept-builder',
      };
    }

    if (subjectsLoading) {
      return {
        ...base,
        children: [{ label: 'Loading...', key: 'loading', accessKey: 'conceptBuilder' }],
      };
    }

    if (subjectMenuItems.length > 0) {
      return {
        ...base,
        children: subjectMenuItems,
      };
    }

    return {
      ...base,
      children: [{ label: 'No subjects available', key: 'empty', accessKey: 'conceptBuilder' }],
    };
  }, [hasClassListAccess, isSuperuserCalc, subjectMenuItems, subjectsLoading]);

  const practiceTestItem = useMemo(() => {
    const base = {
      title: 'Practice Test',
      iconKey: 'quiz',
      key: PRACTICE_TEST_KEY,
      accessKey: 'practiceTest',
      permission: [globalpermissions?.performancePermissions?.testCreator],
    };

    if (isSuperuserCalc || hasClassListAccess) {
      return {
        ...base,
        route: '/r/student/create-test',
      };
    }

    if (subjectsLoading) {
      return {
        ...base,
        children: [{ label: 'Loading...', key: 'loading', accessKey: 'practiceTest' }],
      };
    }

    if (subjectMenuItemsForPracticeTest.length > 0) {
      return {
        ...base,
        children: subjectMenuItemsForPracticeTest,
      };
    }

    return {
      ...base,
      children: [{ label: 'No subjects available', key: 'empty', accessKey: 'practiceTest' }],
    };
  }, [hasClassListAccess, isSuperuserCalc, subjectMenuItemsForPracticeTest, subjectsLoading]);

  return {
    conceptBuilderItem,
    practiceTestItem,
    onConceptBuilderExpand: triggerFetch,
    onPracticeTestExpand: triggerFetch,
    onEdtechExpand: triggerFetch,
    userInfo,
  };
}
